import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Sparkle, Heart } from "@phosphor-icons/react";
import { discoverApi, conciergeApi, travelDateApi } from "@/lib/apiClient";
import { TierBadge } from "@/pages/TravelDate";
import { PASSPORT_DESTINATION_PLACEHOLDER } from "@/constants/destinationImage";

const CORAL = "#F4A28F";

// ─── Trip card matching Dashboard Trips.jsx format ────────────────────────────
function ProfileTripCard({ item, isOwner }) {
    const href = isOwner ? `/trip/${item.id || item.source_id}` : `/discover/trips/${item.id || item.source_id}`;
    const heroImage = item.hero_image || PASSPORT_DESTINATION_PLACEHOLDER;
    const destinations = item.destinations || (item.summary?.route_preview) || [];
    const days = item.days ?? item.summary?.trip_days ?? null;
    const status = item.status || null;
    const totalSpent = item.totals?.total_spent ?? null;
    const emoji = item.emoji || null;
    const title = item.title || (destinations[0] ?? "Trip");

    return (
        <Link
            to={href}
            className="group rounded-2xl overflow-hidden relative transition-transform hover:-translate-y-1"
            style={{ background: "var(--navy-deep)", border: "1px solid rgba(255,255,255,0.06)" }}
            data-testid={`profile-trip-card-${item.id || item.source_id}`}
        >
            <div
                className="aspect-[16/10] bg-cover bg-center"
                style={{ backgroundImage: `url(${heroImage})` }}
            />
            <div
                className="absolute inset-0 pointer-events-none"
                style={{ background: "linear-gradient(180deg, rgba(11,19,32,0) 40%, rgba(5,10,17,0.9) 100%)" }}
            />
            <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/70">
                    {status?.replace("_", " ") || "Planning"}
                    {days != null ? ` · ${days} day${days === 1 ? "" : "s"}` : ""}
                </div>
                <div className="font-serif text-lg text-white leading-tight mt-1 line-clamp-1">
                    {emoji ? `${emoji} ` : ""}{title}
                </div>
                <div className="text-white/70 text-[11px] mt-1 line-clamp-1">
                    {destinations.slice(0, 3).join(" · ")}
                    {totalSpent ? ` · ~$${Math.round(totalSpent).toLocaleString()}` : ""}
                </div>
            </div>
        </Link>
    );
}

// ─── Date card matching TravelDateHistory.jsx format ──────────────────────────
function ProfileDateCard({ item, isOwner }) {
    const href = isOwner ? `/traveldate/${item.id || item.source_id}` : `/discover/dates/${item.id || item.source_id}`;
    const heroImage = item.hero_image || item.summary?.hero_image || null;
    const tier = item.tier ?? item.summary?.date_tier ?? null;
    const score = item.score ?? item.summary?.date_score ?? null;
    const city = item.city || item.destination?.city_name || null;
    const durationHours = item.duration_hours ?? item.summary?.date_duration_hours ?? null;
    const cost = item.estimated_cost_usd ?? null;
    const title = item.title || (city ? `${city} Date` : "TravelDate");

    return (
        <Link
            to={href}
            className="group rounded-3xl overflow-hidden transition-transform hover:-translate-y-1"
            style={{ background: "var(--navy-deep)", border: "1px solid rgba(244,162,143,0.15)" }}
            data-testid={`profile-date-card-${item.id || item.source_id}`}
        >
            <div
                className="aspect-[16/10] bg-cover bg-center relative"
                style={{ backgroundImage: heroImage ? `url(${heroImage})` : "none" }}
            >
                {!heroImage && (
                    <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(244,162,143,0.15), rgba(42,67,101,0.22))" }} />
                )}
                <div
                    className="absolute inset-0"
                    style={{ background: "linear-gradient(180deg, rgba(11,19,32,0) 45%, rgba(5,10,17,0.95) 100%)" }}
                />
                {tier && (
                    <div className="absolute top-3 left-3"><TierBadge tier={tier} /></div>
                )}
                {score != null && (
                    <div
                        className="absolute top-3 right-3 rounded-full px-2 py-0.5 text-[10px] tracking-[0.2em] uppercase"
                        style={{ background: "rgba(11,19,32,0.7)", color: CORAL, border: `1px solid ${CORAL}55`, backdropFilter: "blur(6px)" }}
                    >
                        {score}/100
                    </div>
                )}
                <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="font-serif text-xl text-white leading-tight line-clamp-2">{"❤️"} {title}</div>
                    <div className="text-white/70 text-[11px] mt-1 flex items-center gap-1">
                        <MapPin size={10} weight="fill" color={CORAL} />
                        {[city, durationHours != null ? `${durationHours}h` : null, cost != null ? `$${cost}` : null].filter(Boolean).join(" · ") || "—"}
                    </div>
                </div>
            </div>
        </Link>
    );
}

// ─── Main panel ───────────────────────────────────────────────────────────────
export default function CreatorPlansPanel({ handle, type, isOwner }) {
    const [state, setState] = useState({ items: [], nextCursor: null, loading: true, loadingMore: false });

    const load = useCallback(async (cursor = null) => {
        const setKey = cursor ? "loadingMore" : "loading";
        setState((prev) => ({ ...prev, [setKey]: true }));
        try {
            let items = [];
            let next_cursor = null;

            if (isOwner) {
                // For the profile owner use the native API — gets ALL trips/dates regardless of discover state
                if (type === "trip") {
                    const raw = await conciergeApi.listTrips();
                    items = raw || [];
                } else {
                    const raw = await travelDateApi.history();
                    // history() returns buckets object {planned, completed, favorites, shared}
                    const seen = new Set();
                    items = Object.values(raw || {}).flat().filter((d) => {
                        if (!d?.id || seen.has(d.id)) return false;
                        seen.add(d.id);
                        return true;
                    });
                }
            } else {
                // For visitors use the discover API (only shows discoverable content)
                const apiCall = type === "trip" ? discoverApi.listTrips : discoverApi.listDates;
                const response = await apiCall({ creator_handle: handle, sort: "recommended", cursor, limit: 24 });
                // Map discover items to have a consistent id field
                items = (response.items || []).map((it) => ({ ...it, id: it.source_id }));
                next_cursor = response.next_cursor || null;
            }

            setState((prev) => ({
                items: cursor ? [...prev.items, ...items] : items,
                nextCursor: next_cursor,
                loading: false,
                loadingMore: false,
            }));
        } catch {
            setState({ items: [], nextCursor: null, loading: false, loadingMore: false });
        }
    }, [handle, type, isOwner]);

    useEffect(() => {
        load();
    }, [load]);

    const isTrip = type === "trip";
    const emptyLabel = isTrip ? "No trips yet" : "No dates yet";
    const emptyDesc = isTrip
        ? "Trips created with Passport Concierge will appear here."
        : "TravelDates will appear here.";
    const emptyHref = isTrip ? "/concierge" : "/traveldate";
    const emptyCta = isTrip ? "Plan a Trip" : "Create a Date";

    if (state.loading) {
        return (
            <div className="py-14 text-center text-white/45" data-testid={`profile-${type}-loading`}>
                Loading {isTrip ? "trips" : "dates"}…
            </div>
        );
    }

    if (!state.items.length) {
        return (
            <div
                className="py-16 text-center rounded-[28px]"
                style={{ background: "var(--navy-deep)", border: "1px dashed rgba(255,255,255,0.1)" }}
                data-testid={`profile-${type}-empty`}
            >
                {isTrip
                    ? <Sparkle size={20} color="var(--gold)" className="mx-auto mb-3" weight="fill" />
                    : <Heart size={20} weight="fill" color={CORAL} className="mx-auto mb-3" />
                }
                <div className="text-white/60 mb-4 text-sm">{emptyLabel}</div>
                <p className="text-white/40 text-xs mb-5 max-w-xs mx-auto">{emptyDesc}</p>
                <Link
                    to={emptyHref}
                    className="text-xs !py-2 !px-5 rounded-full font-semibold inline-flex items-center gap-1.5"
                    style={isTrip
                        ? { background: "var(--gold)", color: "var(--navy)" }
                        : { background: CORAL, color: "var(--navy)" }
                    }
                    data-testid={`profile-${type}-empty-cta`}
                >
                    {emptyCta}
                </Link>
            </div>
        );
    }

    return (
        <div className="space-y-5" data-testid={`profile-${type}-panel`}>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {state.items.map((item) => (
                    isTrip
                        ? <ProfileTripCard key={item.id || item.source_id} item={item} isOwner={isOwner} />
                        : <ProfileDateCard key={item.id || item.source_id} item={item} isOwner={isOwner} />
                ))}
            </div>
            {state.nextCursor && (
                <div className="text-center">
                    <button
                        type="button"
                        onClick={() => load(state.nextCursor)}
                        disabled={state.loadingMore}
                        className="btn-ghost text-sm !py-2.5 !px-5"
                        data-testid={`profile-${type}-load-more`}
                    >
                        {state.loadingMore ? "Loading more…" : "Load more"}
                    </button>
                </div>
            )}
        </div>
    );
}
